import React from 'react';
import moment from 'moment';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import { APPRAISER_EVENT_STAGE } from '../../constants/AppraiserEventStage';
import EventCardContent from '../shared/EventCardContent';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { DELIVERY_METHODS } from '../../constants/DeliveryMethodsType';
import { getTooltipContent } from '../shared/TooltipContentProvider';
import { getToday } from '@shared/services/dateService';

/**
 * @typedef {object} MonthlyCalendarEvents
 * @property {string} [OrgUnitNames]
 * @property {string} [AtlName]
 * @property {string} [StartDate]
 * @property {string} [EndDate]
 * @property {*} [Stage]
 * @property {number} [Id]
 * @property {number|string} [DeliveryMethod]
 * @property {Array<*>} [WarningTypes]
 * @property {boolean} [IsAtlAssignedStage]
 */

/**
 * @typedef {object} MonthlyCalendarProps
 * @property {Date} [selectedDate]
 * @property {Array<*>} [weeks]
 * @property {MonthlyCalendarEvents[]} [events]
 */

/**
 * @param {MonthlyCalendarProps} props
 */
const MonthlyCalendar = props => {
  const { events, selectedDate, weeks } = props;

  const navigate = useNavigate();

  const localizer = momentLocalizer(moment);

  const dayPropGetter = date => {
    const disabledDate =
      weeks.length > 0 &&
      !weeks.find(week => isDateWithInRange(date, week.start, week.end)).checked &&
      !events.some(event => isDateWithInRange(date, event.StartDate, event.EndDate));

    return { className: cx({ disabled: disabledDate }) };
  };

  const isDateWithInRange = (date, start, end) => {
    return moment(date).startOf('day').isBetween(moment(start), moment(end), null, '[]');
  };

  const dateHeader = ({ label }) => <span>{label}</span>;

  const isPastEvent = eventStartDate => moment(eventStartDate).isSameOrBefore(getToday(), 'day');

  const calendarEvents = events.map(event => {
    const deliveryMethod = Object.values(DELIVERY_METHODS).find(
      x => x.value === event.DeliveryMethod
    );

    return {
      title: (
        <EventCardContent
          eventId={event.Id}
          atlName={event.AtlName}
          orgUnitNames={event.OrgUnitNames}
          deliveryMethod={deliveryMethod ? deliveryMethod.label : null}
          warningTypes={event.WarningTypes}
        />
      ),
      start: moment(event.StartDate).toDate(),
      end: moment(event.EndDate).add(1, 'days').toDate(),
      stage: event.Stage,
      IsAtlAssignedStage: event.IsAtlAssignedStage,
      eventId: event.Id,
      tooltipContent: getTooltipContent(event, deliveryMethod)
    };
  });

  const handleClickEvent = event => {
    if (isPastEvent(event.start)) {
      return;
    }
    navigate(`/calendar/edit-event/${event.eventId}`);
  };

  const eventPropGetter = event => {
    return {
      className: cx('event-card', {
        'appraisal-assigned': event.IsAtlAssignedStage,
        'team-review': event.stage == APPRAISER_EVENT_STAGE.TEAM_REVIEW_OPEN_FOR_BID,
        'team-assigned': event.stage === APPRAISER_EVENT_STAGE.TEAM_ASSIGNED,
        'ready-to-deliver': event.stage === APPRAISER_EVENT_STAGE.READY_TO_DELIVER,
        'disabled-event': isPastEvent(event.start)
      })
    };
  };

  return (
    <div className='monthly-calendar'>
      <Calendar
        className='calendar'
        localizer={localizer}
        style={{ height: 600 }}
        toolbar={false}
        date={selectedDate}
        defaultView={Views.MONTH}
        onNavigate={() => {}} // removing the warning
        tooltipAccessor={event => event.tooltipContent}
        components={{ month: { dateHeader: dateHeader } }}
        events={calendarEvents}
        eventPropGetter={eventPropGetter}
        onSelectEvent={handleClickEvent}
        dayPropGetter={dayPropGetter}
      />
    </div>
  );
};

export default MonthlyCalendar;
